import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { ObjectFieldValues } from "../../../../api/types/objectTypes";
import type { DocumentPollResult, DocumentPollingScheduler } from "./documentPolling";

// Accepted file extensions for document upload dialogs
export const acceptedFileExtensions = [
  ".pdf",
  ".docx",
  ".doc",
  ".xlsx",
  ".xlsm",
  ".xls",
  ".pptx",
  ".ppt",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
];

export const uploadDocumentWorkflowFile = withErrorHandling(adminApi.uploadDocumentWorkflowFile);
export const matchWorkflowDocuments = withErrorHandling(adminApi.matchWorkflowDocuments);
export const runDocumentWorkflows = withErrorHandling(adminApi.runDocumentWorkflows);

const getDocumentWorkflowBySourceId = withErrorHandling(adminApi.getDocumentWorkflowBySourceId);

const searchObjects = withErrorHandling(adminApi.searchObjects);

export const findInvoiceByCatalogueId = async (catalogueId: string): Promise<ObjectFieldValues | undefined> => {
  const [resp, error] = await searchObjects("Invoice", {
    range: { skip: 0, take: 2 },
    filters: [{ fieldId: "DataCatalogId", guidFilter: { value: catalogueId } }],
  });

  if (error) {
    logError(error, "[documentWorkflowService] findInvoiceByCatalogueId");
    return undefined;
  }

  return resp.items.length === 1 ? defined(resp.items[0]) : undefined;
};

// Polling constants
export const POLL_INTERVAL_MS = 3_000;
export const POLL_TIMEOUT_MS = 300_000;
export const MAX_POLL_CONCURRENCY = 10;

// Generic file classification
export interface ClassifiedFiles<T> {
  unique: T[];
  duplicates: T[];
}

export const classifyNewFiles = <T extends { file: File }>(
  newFiles: File[],
  existingEntries: readonly T[],
  createEntry: (file: File) => T,
  createDuplicate: (file: File) => T
): ClassifiedFiles<T> => {
  const existingNames = new Set(existingEntries.map((entry) => entry.file.name.toLowerCase()));
  const seen = new Set<string>();
  const unique: T[] = [];
  const duplicates: T[] = [];

  for (const file of newFiles) {
    const normalizedName = file.name.toLowerCase();
    if (existingNames.has(normalizedName) || seen.has(normalizedName)) {
      duplicates.push(createDuplicate(file));
    } else {
      seen.add(normalizedName);
      unique.push(createEntry(file));
    }
  }

  return { unique, duplicates };
};

// Shared pollBySourceId implementation
export const pollBySourceId = async (sourceId: string): Promise<DocumentPollResult> => {
  const [result, error] = await getDocumentWorkflowBySourceId(sourceId);
  if (error) {
    return { kind: "Error", error };
  }

  if (!result.workflowInfo) {
    return { kind: "NotFound" };
  }

  return {
    kind: "Started",
    workflow: {
      workflowId: result.workflowInfo.workflowId,
      templateId: result.workflowInfo.templateId,
      workflowType: result.workflowInfo.workflowType,
    },
  };
};

// Queue result processing
type DocumentWorkflowQueueStatus = "Queued" | "Failed";

interface DocumentWorkflowQueueResultItem {
  status: DocumentWorkflowQueueStatus;
  sourceId?: string;
  errorMessage?: string;
}

export const processQueueResults = (
  files: readonly { id: string }[],
  resultItems: readonly DocumentWorkflowQueueResultItem[],
  scheduler: Pick<DocumentPollingScheduler, "upsert" | "remove">,
  onQueued: (id: string, sourceId?: string) => void,
  onFailed: (id: string, error: string) => void
): void => {
  for (let index = 0; index < files.length; index++) {
    const file = files[index];
    if (!file) {
      continue;
    }

    const item = resultItems[index];

    if (!item) {
      scheduler.remove(file.id);
      onFailed(file.id, "Missing queue result item.");
      continue;
    }

    if (item.status === "Queued") {
      onQueued(file.id, item.sourceId);

      if (item.sourceId) {
        scheduler.upsert(file.id, item.sourceId);
      } else {
        scheduler.remove(file.id);
      }

      continue;
    }

    scheduler.remove(file.id);
    onFailed(file.id, item.errorMessage ?? "Failed to queue workflow.");
  }
};
